import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ClientDTO,
  DailyAppsMember,
  GoogleReviewDTO,
  GoogleReviewSnapshotDTO,
  GoogleReviewsCountersDTO,
  GoogleReviewsState,
  LeaderboardRowDTO,
  ReviewLinkSettings,
  ReviewQrLinkDTO,
  SmsConfigDTO,
} from '../type';

const initialState: GoogleReviewsState = {
  reviews: [],
  total: 0,
  counters: { total: 0, confirmed: 0, pending: 0, smsSent: 0, scans: 0, shares: 0 },
  smsConfig: null,
  clients: [],
  reviewLink: null,
  qrLink: null,
  leaderboard: [],
  snapshots: [],
  dailyAppsMembers: [],
  isLoading: false,
  hasError: false,
  isCountersLoading: false,
  hasCountersError: false,
  isReviewLinkLoading: false,
  hasReviewLinkError: false,
  isQrLinkLoading: false,
  hasQrLinkError: false,
  isLeaderboardLoading: false,
  hasLeaderboardError: false,
  isSnapshotsLoading: false,
  hasSnapshotsError: false,
  isDailyAppsMembersLoading: false,
  hasDailyAppsMembersError: false,
};

export const { actions, reducer } = createSlice({
  name: 'googleReviews',
  initialState,
  reducers: {
    setReviews: (state, action: PayloadAction<{ data: GoogleReviewDTO[]; total: number }>) => {
      state.reviews = action.payload.data;
      state.total = action.payload.total;
      state.hasError = false;
    },
    setReviewConfirmed: (state, action: PayloadAction<{ id: string; confirmed: boolean }>) => {
      const review = state.reviews.find(item => item.id === action.payload.id);
      if (review) review.isConfirmedOnGoogle = action.payload.confirmed;
    },
    markSmsSent: (state, action: PayloadAction<string>) => {
      const review = state.reviews.find(item => item.id === action.payload);
      if (review) review.isSmsSent = true;
    },
    adjustConfirmedCounter: (state, action: PayloadAction<number>) => {
      state.counters.confirmed = Math.max(0, state.counters.confirmed + action.payload);
      state.counters.pending = Math.max(0, state.counters.pending - action.payload);
    },
    adjustSmsCounter: (state, action: PayloadAction<number>) => {
      state.counters.smsSent += action.payload;
    },
    setCounters: (state, action: PayloadAction<GoogleReviewsCountersDTO>) => {
      state.counters = action.payload;
      state.hasCountersError = false;
    },
    setSmsConfig: (state, action: PayloadAction<SmsConfigDTO>) => {
      state.smsConfig = action.payload;
    },
    setClients: (state, action: PayloadAction<ClientDTO[]>) => {
      state.clients = action.payload;
    },
    setReviewLink: (state, action: PayloadAction<ReviewLinkSettings | null>) => {
      state.reviewLink = action.payload;
      state.hasReviewLinkError = false;
    },
    setDailyAppsMembers: (state, action: PayloadAction<DailyAppsMember[]>) => {
      state.dailyAppsMembers = action.payload;
      state.hasDailyAppsMembersError = false;
    },
    upsertDailyAppsMember: (state, action: PayloadAction<DailyAppsMember>) => {
      const index = state.dailyAppsMembers.findIndex(member => member.id === action.payload.id);
      if (index === -1) state.dailyAppsMembers.push(action.payload);
      else state.dailyAppsMembers[index] = action.payload;
    },
    removeDailyAppsMember: (state, action: PayloadAction<string>) => {
      state.dailyAppsMembers = state.dailyAppsMembers.filter(member => member.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<boolean>) => {
      state.hasError = action.payload;
    },
    setCountersLoading: (state, action: PayloadAction<boolean>) => {
      state.isCountersLoading = action.payload;
    },
    setCountersError: (state, action: PayloadAction<boolean>) => {
      state.hasCountersError = action.payload;
    },
    setQrLink: (state, action: PayloadAction<ReviewQrLinkDTO | null>) => {
      state.qrLink = action.payload;
      state.hasQrLinkError = false;
    },
    setQrLinkLoading: (state, action: PayloadAction<boolean>) => {
      state.isQrLinkLoading = action.payload;
    },
    setQrLinkError: (state, action: PayloadAction<boolean>) => {
      state.hasQrLinkError = action.payload;
    },
    setLeaderboard: (state, action: PayloadAction<LeaderboardRowDTO[]>) => {
      state.leaderboard = action.payload;
      state.hasLeaderboardError = false;
    },
    setLeaderboardLoading: (state, action: PayloadAction<boolean>) => {
      state.isLeaderboardLoading = action.payload;
    },
    setLeaderboardError: (state, action: PayloadAction<boolean>) => {
      state.hasLeaderboardError = action.payload;
    },
    setSnapshots: (state, action: PayloadAction<GoogleReviewSnapshotDTO[]>) => {
      state.snapshots = action.payload;
      state.hasSnapshotsError = false;
    },
    setSnapshotsLoading: (state, action: PayloadAction<boolean>) => {
      state.isSnapshotsLoading = action.payload;
    },
    setSnapshotsError: (state, action: PayloadAction<boolean>) => {
      state.hasSnapshotsError = action.payload;
    },
    setReviewLinkLoading: (state, action: PayloadAction<boolean>) => {
      state.isReviewLinkLoading = action.payload;
    },
    setReviewLinkError: (state, action: PayloadAction<boolean>) => {
      state.hasReviewLinkError = action.payload;
    },
    setDailyAppsMembersLoading: (state, action: PayloadAction<boolean>) => {
      state.isDailyAppsMembersLoading = action.payload;
    },
    setDailyAppsMembersError: (state, action: PayloadAction<boolean>) => {
      state.hasDailyAppsMembersError = action.payload;
    },
  },
});
